import React, { Suspense, useRef, useEffect, useState } from "react";
import {
  Grid,
  Segment,
  Image,
  Tab,
  Icon,
  Button,
  Form,
  Input,
  Modal,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { storeGet, storeSet } from "../../crud/storage";
import { details, login, register, forgot } from "../../crud/user";
import { SemanticToastContainer, toast } from "react-semantic-toasts";

import { default as config } from "../../constants/config.json";
import { useHistory, useLocation } from "react-router-dom";
import LazyLoad from "react-lazyload";
import LayoutEnter from "../../layout/Layoutenter";
import "semantic-ui-css/semantic.min.css";

import "./Register.css";
export default function Register() {
  let history = useHistory();
  let location = useLocation();

  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [openedUserModal, setOpenedUserModal] = useState(false);
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState("0");
  const [submittingQoute, setSubmittingQoute] = useState(false);
  const [modalSuccessCallback, setModalSuccessCallback] = useState();
  const [userFormValues, setUserFormValues] = useState({
    name: "",
    mobile: "",
    email: "",
  });
  const [redirectToResult, setRedirectToResult] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [tosError, setTosError] = useState("");
  const [refCode, setRefCode] = useState('');
  const [referralAcknowledged, setReferralAcknowledged] = useState(false);

  useEffect(() => {
    window.gtag("config", config.GoogleAnalytics.code, {
      page_path: window.location.hash,
    });

    //get the last form
    if (location.state) {
      setRedirectToResult(location.state);
      // Safety net: persist quote data to localStorage immediately
      // so it survives even if the user navigates away before registering
      storeSet("last_result", location.state);
    }
  }, [location]);

  const _userDetails = () => {
    details()
      .then((response) => {
        setUserData(response.data.success);
      })
      .catch((error) => {
        setSubmittingQoute(false);
        if (error.response.status === 401) {
          storeSet("user", null);
        } else {
          if (error.response.data && error.response.data.message) {
            toast({
              type: "error",
              title: error.response.data.message,
              animation: "bounce",
              time: 5000,
            });
          }
        }
      });
  };

  useEffect(() => {
    const userStorage = storeGet("user");
    if (userStorage && userStorage.token) {
      _userDetails();
    }
  }, []);

  useEffect(() => {
    const hashParts = window.location.hash.split('?');
    if (hashParts[1]) {
      const params = new URLSearchParams(hashParts[1]);
      const ref = params.get('ref') || '';
      if (ref) setRefCode(ref);
    }
  }, []);

  const userFieldChanged = (e) => {
    let field = {};
    field[e.target.name] = e.target.value;
    let values = { ...userFormValues, ...field };
    setUserFormValues(values);
    if (e.target.name === "mobile" && phoneError) {
      setPhoneError("");
    }
    if (e.target.name === "password" && passwordError) {
      setPasswordError("");
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return null;
    if (password.length < 8) return { label: "Too short", color: "#c81e1e" };
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSymbols = /[^a-zA-Z0-9]/.test(password);
    const types = [hasLetters, hasNumbers, hasSymbols].filter(Boolean).length;
    if (types >= 2) return { label: "Strong", color: "#2d7a47" };
    return { label: "Medium", color: "#b7830a" };
  };

  const userActions = {
    openUserModal: ({ successCallback, successParams }) => {
      setOpenedUserModal(true);
      setUserFormLoading(false);
      setModalSuccessCallback({
        successCallback: successCallback,
        successParams: successParams,
      });
    },
    logout: () => {
      storeSet("user", null);
      setUserData(null);
      history.push("/login");
    },
  };

  const loginEmailEl = useRef();

  const haveAccount = () => {
    if (redirectToResult) {
      //setGoToResult(true)
      history.push({
        pathname: "/login",
        state: {
          pathname: redirectToResult.pathname,
          formValues: redirectToResult.formValues,
          resultsProp: redirectToResult.resultsProp,
          loading: redirectToResult.loading,
          dontShowButtons: redirectToResult.dontShowButtons,
        },
      });
    } else {
      //setGoToResult(false)
      history.push("/login");
    }
  };

  const CANADIAN_AREA_CODES = [
    '204','226','236','249','250','263','289','306','343','354',
    '365','367','368','382','388','403','416','418','428','431',
    '437','438','450','468','474','506','514','519','548','579',
    '581','584','587','604','613','639','647','672','683','705',
    '709','742','753','778','780','782','807','819','825','867',
    '873','879','902','905',
  ];

  const validateCanadianPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    const normalized = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
    if (normalized.length !== 10) return false;
    return CANADIAN_AREA_CODES.includes(normalized.slice(0, 3));
  };

  const _register = () => {
    if (!validateCanadianPhone(userFormValues.mobile)) {
      setPhoneError("GetCovered is currently available for Canadian residents only. Please enter a valid Canadian phone number (e.g. 416-555-1234)");
      return;
    }
    setPhoneError("");

    if (!userFormValues.password || userFormValues.password.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    setPasswordError("");

    if (!tosAccepted) {
      setTosError("You must accept the Terms of Service and Privacy Policy to create an account.");
      return;
    }
    setTosError("");

    setUserFormLoading(true);
    register({ ...userFormValues, marketing_consent: marketingConsent, ...(refCode ? { referral_code: refCode } : {}) })
      .then((response) => {
        //if it has the previous result save it for the next login
        if (redirectToResult) {
          storeSet("last_result", redirectToResult);
        }
        setUserFormLoading(false);

        if (response.data && response.data.success) {
          toast({
            type: "success",
            title:
              "We sent a verification code to your email. Please enter it to complete registration.",
            animation: "bounce",
            time: 5000,
          });

          // Redirect to verify page after short delay so user can see the toast
          setTimeout(() => {
            history.push("/verify");
          }, 1500);
        }
      })
      .catch((error) => {
        setUserFormLoading(false);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          toast({
            type: "error",
            title: error.response.data.error,
            animation: "bounce",
            time: 5000,
          });
        }
      });
  };

  const handleTabChange = (e, { activeIndex }) => setActiveIndex(activeIndex);

  return (
    <LayoutEnter>
      <SemanticToastContainer position="bottom-right" />
      <div class="centerdiv">
        <div class="ui two column centered grid">
          <Grid className="mx-0 padded">
            <Grid.Row
              className="section-wrapper insurance-type have-icon"
              style={{ padding: "0px" }}
            >
              <Grid.Column
                computer={14}
                tablet={16}
                mobile={14}
                style={{ margin: "0px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "left",

                    width: "100%",
                  }}
                >
                  <NavLink
                    to={`/`}
                    style={{
                      display: "flex",
                      justifyContent: "left",
                    }}
                  >
                    <Icon name="left arrow" className="mx-3 " />
                    <p className="titletext">{t(`back_home`)}</p>
                  </NavLink>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className="section-wrapper insurance-type have-icon">
              <Grid.Column
                computer={14}
                tablet={16}
                mobile={14}
                style={{ margin: "0px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "left",

                    width: "100%",
                  }}
                >
                  <p className="logintitle">{t(`Sign_up`)}</p>
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className="section-wrapper insurance-type have-icon">
              <Grid.Column
                computer={12}
                tablet={14}
                mobile={12}
                style={{ margin: "20px 0px", padding: "0px" }}
              >
                {refCode && !referralAcknowledged ? (
                  <div style={{
                    background: '#EFF6FF',
                    borderLeft: '4px solid #0f1f42',
                    borderRadius: 8,
                    padding: 20,
                    marginBottom: 16,
                  }}>
                    <p style={{ fontSize: 14, color: '#0f1f42', lineHeight: 1.6, margin: '0 0 16px' }}>
                      You were referred to GetCovered by a friend. The person who referred you receives access to premium financial planning tools as a result of this referral. This does not affect the quality of service or information you receive.
                    </p>
                    <button
                      onClick={() => setReferralAcknowledged(true)}
                      style={{
                        background: '#0f1f42',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '10px 24px',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Continue to Registration
                    </button>
                  </div>
                ) : (
                <Form onSubmit={_register} style={{ width: "100%" }}>
                  <Form.Field required>
                    <Input
                      className="logininputborder"
                      required
                      name="name"
                      icon="info"
                      iconPosition="left"
                      placeholder={t("main.name")}
                      value={userFormValues.name}
                      onChange={userFieldChanged}
                    />
                  </Form.Field>

                  <Form.Field required error={!!phoneError}>
                    <Input
                      className="logininputborder"
                      required
                      name="mobile"
                      icon="mobile alternate"
                      iconPosition="left"
                      placeholder={t("main.mobile")}
                      value={userFormValues.mobile}
                      onChange={userFieldChanged}
                      style={phoneError ? { border: "1px solid #c81e1e", borderRadius: "4px" } : {}}
                    />
                    {phoneError && (
                      <p style={{ color: "#c81e1e", fontSize: "12px", marginTop: "6px", marginBottom: "0" }}>
                        {phoneError}
                      </p>
                    )}
                  </Form.Field>

                  <Form.Field>
                    <Input
                      className="logininputborder"
                      required
                      name="email"
                      icon="mail"
                      iconPosition="left"
                      placeholder={t("main.email")}
                      value={userFormValues.email}
                      onChange={userFieldChanged}
                    />
                  </Form.Field>

                  <Form.Field error={!!passwordError}>
                    <Input
                      className="logininputborder"
                      required
                      name="password"
                      type="password"
                      icon="lock"
                      iconPosition="left"
                      placeholder={t("main.password")}
                      onChange={userFieldChanged}
                      style={passwordError ? { border: "1px solid #c81e1e", borderRadius: "4px" } : {}}
                    />
                    {passwordError && (
                      <p style={{ color: "#c81e1e", fontSize: "12px", marginTop: "6px", marginBottom: "0" }}>
                        {passwordError}
                      </p>
                    )}
                    {userFormValues.password && !passwordError && (() => {
                      const strength = getPasswordStrength(userFormValues.password);
                      return strength ? (
                        <div style={{ marginTop: "6px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ flex: 1, height: "4px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
                              <div style={{
                                width: strength.label === "Too short" ? "30%" : strength.label === "Medium" ? "60%" : "100%",
                                height: "100%",
                                background: strength.color,
                                borderRadius: "2px",
                                transition: "width 0.2s"
                              }} />
                            </div>
                            <span style={{ fontSize: "11px", fontWeight: "600", color: strength.color }}>{strength.label}</span>
                          </div>
                          <p style={{ fontSize: "11px", color: "#718096", marginTop: "4px", marginBottom: "0" }}>
                            Minimum 8 characters required
                          </p>
                        </div>
                      ) : null;
                    })()}
                  </Form.Field>

                  {/* Terms of Service & Privacy Policy (required) */}
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, color: '#555' }}>
                      <input
                        type="checkbox"
                        checked={tosAccepted}
                        onChange={(e) => { setTosAccepted(e.target.checked); if (e.target.checked) setTosError(''); }}
                        style={{ marginTop: 3, accentColor: '#0f1f42' }}
                      />
                      <span>
                        I agree to the{' '}
                        <a href="/#/Term-of-use" style={{ color: '#266ab3', textDecoration: 'underline' }}>Terms of Service</a>
                        {' '}and{' '}
                        <a href="/#/Privacy-policy" style={{ color: '#266ab3', textDecoration: 'underline' }}>Privacy Policy</a>
                      </span>
                    </label>
                    {tosError && (
                      <p style={{ color: '#c81e1e', fontSize: 12, marginTop: 4, marginBottom: 0 }}>
                        {tosError}
                      </p>
                    )}
                  </div>

                  {/* CASL Marketing Consent (optional) */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, color: '#555' }}>
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={(e) => setMarketingConsent(e.target.checked)}
                        style={{ marginTop: 3, accentColor: '#0f1f42' }}
                      />
                      <span>
                        I agree to receive educational content and updates from AplusWealth Inc.
                      </span>
                    </label>
                  </div>

                  <div className="regdivider">
                    <Button
                      type="button"
                      style={{ textAlign: "left", marginLeft: "-20px" }}
                      className="buttonforget"
                      disabled={userFormLoading}
                      onClick={() => {
                        haveAccount();
                      }}
                    >
                      {t("have_account")}
                    </Button>

                    <Button
                      type="submit"
                      primary
                      loading={userFormLoading}
                      disabled={userFormLoading}
                      className="buttonlogin"
                    >
                      {t("Sign_up")}
                    </Button>
                  </div>
                </Form>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </div>
    </LayoutEnter>
  );
}
